import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  Compass,
  Sparkles,
  Users,
  Quote,
  Brain,
  Feather,
  Flame,
} from "lucide-react";
import { luminarc, images } from "../mock/mock";

const principleIcons = [Sparkles, Users, Feather, Compass, Brain, Flame];

export default function Luminarc() {
  return (
    <main className="bg-[var(--cream)] text-[var(--ink)]">
      {/* HERO — 7/5 split with visual card */}
      <section className="pt-10 md:pt-14 pb-16 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 md:gap-12 items-stretch">
            {/* LEFT — content */}
            <div className="col-span-12 md:col-span-7">
              <div className="tag-mono mb-6">[ Luminarc™ · Flagship experience ]</div>
              <h1
                style={{
                  fontFamily: "Inter",
                  fontWeight: 800,
                  fontSize: "clamp(2.8rem, 6.2vw, 6.4rem)",
                  lineHeight: 0.94,
                  letterSpacing: "-0.045em",
                }}
              >
                {luminarc.trademark}
                <br />
                <span className="italic-serif text-[var(--molten)]">{luminarc.tagline}</span>
              </h1>
              <p className="mt-8 md:mt-10 text-lg md:text-xl text-[var(--ink)]/75 leading-relaxed max-w-xl">
                {luminarc.hero}
              </p>
              <div className="mt-10 flex gap-3 flex-wrap">
                <Link to="/contact" className="btn-ink">
                  <span>Express interest</span> <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>

            {/* RIGHT — visual card + program meta */}
            <div className="col-span-12 md:col-span-5 flex flex-col gap-5">
              <div className="relative flex-1 min-h-[320px] md:min-h-[420px] overflow-hidden">
                <img
                  src={images.workshopClass}
                  alt="Luminarc cohort"
                  className="absolute inset-0 w-full h-full object-cover grayscale"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/85 via-[var(--ink)]/20 to-transparent" />
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 bg-[var(--cream)]/95 backdrop-blur px-3 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--molten)] animate-pulse" />
                  <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--ink)]">
                    Next intake · Q2 2026
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="text-[var(--cream)]" style={{ fontFamily: "Inter", fontWeight: 800, fontSize: "2rem", letterSpacing: "-0.04em", lineHeight: 1 }}>
                    Not a workshop.
                  </div>
                  <div className="italic-serif text-[var(--molten)] mt-1" style={{ fontSize: "1.6rem", letterSpacing: "-0.025em" }}>
                    A transformation.
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-0 border-t border-[var(--ink)]/15">
                <div className="py-5 px-4 border-r border-[var(--ink)]/15">
                  <div className="tag-mono mb-1">Format</div>
                  <div className="font-serif text-xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>Hybrid</div>
                </div>
                <div className="py-5 px-4 border-r border-[var(--ink)]/15">
                  <div className="tag-mono mb-1">Seats</div>
                  <div className="font-serif text-xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>Limited</div>
                </div>
                <div className="py-5 px-4">
                  <div className="tag-mono mb-1">Lead</div>
                  <div className="font-serif text-xl" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>Dr. Drijesh P.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NOT A WORKSHOP — TRANSFORMATION */}
      <section className="section-ink grain py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-6">[ What is Luminarc™ ]</div>
          <div className="grid grid-cols-12 gap-8 items-end">
            <h2 className="headline-xl text-[var(--cream)] col-span-12 md:col-span-9">
              Not a workshop.{" "}
              <span className="italic-serif text-[var(--molten)]">A transformation.</span>
            </h2>
            <div className="col-span-12 md:col-span-3 md:text-right">
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--cream)]/55">
                Category-defining
                <br />
                experience
              </div>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-12 gap-8 text-[var(--cream)]/80 text-lg leading-relaxed">
            <p className="col-span-12 md:col-span-6">{luminarc.long}</p>
            <p className="col-span-12 md:col-span-6">{luminarc.pitch}</p>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-4">[ Luminarc™ principles ]</div>
          <h2 className="headline-lg mb-14 max-w-3xl">
            Six principles that define the{" "}
            <span className="italic-serif text-[var(--molten)]">experience.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[var(--ink)]/15">
            {luminarc.principles.map((p, i) => {
              const Icon = principleIcons[i % principleIcons.length];
              const col = i % 3;
              const isLastRow = i >= luminarc.principles.length - (luminarc.principles.length % 3 || 3);
              return (
                <div
                  key={i}
                  className={`rise p-8 md:p-10 border-b border-[var(--ink)]/15 hover:bg-[var(--cream-soft)] transition-colors duration-500 ${
                    col < 2 ? "md:border-r" : ""
                  }`}
                >
                  <div className="flex items-center justify-between mb-7">
                    <span className="num-chip">{String(i + 1).padStart(2, "0")}</span>
                    <Icon size={20} className="text-[var(--molten)]" />
                  </div>
                  <div className="font-serif text-2xl md:text-3xl leading-tight mb-3">{p.t}</div>
                  <p className="text-[var(--ink)]/70 leading-relaxed">{p.c}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="py-24 md:py-32 grain bg-[var(--cream-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 items-end mb-12">
            <div className="col-span-12 md:col-span-8">
              <div className="tag-mono mb-4">[ Expected outcomes ]</div>
              <h2 className="headline-lg">
                What you’ll <span className="italic-serif text-[var(--molten)]">gain.</span>
              </h2>
            </div>
            <p className="col-span-12 md:col-span-4 text-[var(--ink)]/70 leading-relaxed">
              Participants emerge with new mental models and practical skills that transform how they lead, communicate, and decide.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {luminarc.outcomes.map((o, i) => (
              <div
                key={i}
                className="rise p-8 border border-[var(--ink)]/15 bg-[var(--cream)] hover:border-[var(--molten)] transition-colors"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="num-chip">{String(i + 1).padStart(2, "0")}</span>
                  <CheckCircle2 size={20} className="text-[var(--molten)]" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight">{o.t}</h3>
                <p className="mt-4 text-[var(--ink)]/70 leading-relaxed">{o.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 items-end mb-12">
            <div className="col-span-12 md:col-span-7">
              <div className="tag-mono mb-4">[ Who it’s for ]</div>
              <h2 className="headline-lg">
                For leaders at{" "}
                <span className="italic-serif text-[var(--molten)]">every stage.</span>
              </h2>
              <p className="mt-6 text-[var(--ink)]/75 max-w-xl leading-relaxed">
                Luminarc™ is designed for anyone who faces complex decisions in uncertain environments — from seasoned executives to emerging professionals and ambitious students.
              </p>
              <p className="mt-4 italic-serif text-xl md:text-2xl text-[var(--molten)] max-w-xl">
                If you ask <span>“what story should we tell?”</span> more often than <span>“what does the data say?”</span> — Luminarc™ is for you.
              </p>
            </div>

            <div className="col-span-12 md:col-span-5">
              <img
                src={images.workshopClass}
                alt="Luminarc cohort"
                className="w-full h-80 md:h-[28rem] object-cover grayscale"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border-t border-[var(--ink)]/15 mt-4">
            {luminarc.audience.map((a, i) => (
              <div
                key={i}
                className={`rise py-6 md:py-8 px-4 md:px-8 border-b border-[var(--ink)]/15 flex items-center gap-4 ${
                  (i + 1) % 3 !== 0 ? "md:border-r" : ""
                } ${(i + 1) % 2 !== 0 ? "border-r md:border-r" : ""}`}
              >
                <span className="font-serif text-2xl md:text-3xl text-[var(--molten)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif text-lg md:text-xl">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VOICES */}
      <section className="section-ink grain py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-4">[ Participant voices ]</div>
          <h2 className="headline-lg text-[var(--cream)] max-w-3xl mb-14">
            From those who’ve{" "}
            <span className="italic-serif text-[var(--molten)]">experienced</span> the transformation.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {luminarc.voices.map((v, i) => (
              <blockquote
                key={i}
                className="rise p-8 border border-[var(--cream)]/20 hover:border-[var(--molten)] transition-colors"
              >
                <Quote size={22} className="text-[var(--molten)] mb-5" />
                <p className="font-serif text-xl md:text-2xl leading-snug text-[var(--cream)]">
                  {v.quote}
                </p>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="tag-mono mb-4">[ Ready to see beyond the data? ]</div>
            <h2 className="headline-lg max-w-3xl">
              Luminarc™ cohorts are <span className="italic-serif text-[var(--molten)]">intentionally limited.</span>
            </h2>
            <p className="mt-6 text-[var(--ink)]/75 max-w-2xl leading-relaxed">
              Express your interest to learn about upcoming programs and reserve your seat.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <Link to="/contact" className="btn-ink">
              Express interest <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
